import { traceEvent } from '@wk/wk-gatherer';
import { Button } from '@wk/wk-uni-lib';
import { useTranslation } from 'next-i18next';
import React, { useCallback, useContext, useEffect, useState } from 'react';

import LeadButton from '@/components/LeadButton';
import TrustpilotGrid from '@/components/TrustpilotBar/Grid';
import TrustpilotSlider from '@/components/TrustpilotBar/Slider';
import { GlobalLoginContext } from '@/context/GlobalLoginContest';
import useIsMobileNew from '@/hooks/useIsMobileNew';

/**
 * 评论区模块
 * PC 展示 Trustpilot 官方 Grid；
 * 移动端用横向 Slider（Grid 在 iframe 内部纵向滚动，滚到边界后
 * 不会把滚动传递给页面，导致页面卡住无法继续上下滑）
 */
const ReviewsSection: React.FC = () => {
  const { t, i18n } = useTranslation('wukong-reviews');
  const isZh = i18n.language === 'zh';
  const { isLogin } = useContext(GlobalLoginContext);
  const isMobile = useIsMobileNew();
  // SSR/水合完成前 isMobile 恒为 false，不能作为渲染依据，
  // 否则静态 HTML 在手机上会先展示（甚至停留在）Grid
  const [mounted, setMounted] = useState(false);

  useEffect(() => {
    setMounted(true);
  }, []);

  const handleBookClassTrace = useCallback(() => {
    traceEvent({
      data: {
        _event: 'C_WallofLove_BookClass',
        dsSource: '用户之声-好评墙',
        loginStatus: isLogin ? '已登' : '未登',
      },
    });
  }, [isLogin]);

  return (
    <section
      id="reviews"
      className="flex w-full flex-col items-center space-y-6 px-4 py-[50px] lg:space-y-10 lg:px-8 lg:py-[70px] xl:space-y-12 xl:px-20 xl:py-[90px]"
      style={{ backgroundColor: '#F7F4ED' }}>
      <h2
        className={`text-center text-[28px] font-black leading-normal text-text-primary lg:text-[34px] xl:text-[48px] [&>span]:text-brand-red-100 ${isZh ? 'font-sourceHanSans' : ''}`}
        dangerouslySetInnerHTML={{ __html: t('reviews-标题') }}
      />

      <div className="w-full max-w-[984px] xl:max-w-[1320px]">
        {mounted ? (
          // 挂载后按真实视口只保留一个，避免两个 widget 同时初始化
          isMobile ? (
            <TrustpilotSlider variant="overallExperience" />
          ) : (
            <TrustpilotGrid />
          )
        ) : (
          // 首帧（含 SSR 静态 HTML）用 CSS 断点区分，手机上第一眼就是 Slider
          <>
            <TrustpilotSlider
              variant="overallExperience"
              className="lg:hidden"
            />
            <TrustpilotGrid className="hidden lg:block" />
          </>
        )}
      </div>

      {/* eslint-disable-next-line jsx-a11y/click-events-have-key-events, jsx-a11y/no-static-element-interactions */}
      <div onClick={handleBookClassTrace}>
        <LeadButton
          asCustomButton
          callbackUrl={
            typeof window !== 'undefined' ? window.location.href : ''
          }
          traceName="O_WallOfLove_ReviewsSection_CTAClick">
          <Button
            size="large"
            className="h-[52px] px-[30px] text-[20px] font-bold lg:h-[55px] lg:px-12 lg:text-[18px] xl:h-[77px] xl:px-[64px] xl:text-[24px]">
            {t('reviews-按钮')}
          </Button>
        </LeadButton>
      </div>
    </section>
  );
};

export default ReviewsSection;
